import React, { useEffect, useState, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Palette, RotateCcw, Check, Eye, Sparkles, Type, Square, Circle, RectangleHorizontal, ImageIcon, Upload, Trash2, X as XIcon, Save } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useTheme, ThemeConfig } from '../../context/ThemeContext';
function ColorPicker({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return <div className="flex items-center justify-between gap-4" data-id="element-1243">
      <label className="text-sm text-slate-300 font-medium" data-id="element-1244">{label}</label>
      <div className="flex items-center gap-2" data-id="element-1245">
        <div className="relative" data-id="element-1246">
          <input type="color" value={value} onChange={e => onChange(e.target.value)} className="w-8 h-8 rounded-lg border border-slate-700 cursor-pointer bg-transparent appearance-none [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded-lg [&::-webkit-color-swatch]:border-none" data-id="element-1247" />
        </div>
        <input type="text" value={value} onChange={e => onChange(e.target.value)} className="w-24 bg-slate-950 border border-slate-800 rounded-lg px-2 py-1 text-xs text-white font-mono focus:border-fox-500 focus:outline-none" data-id="element-1248" />
      </div>
    </div>;
}
export function ThemeEditor() {
  const {
    currentTheme,
    setTheme,
    prebuiltThemes,
    resetTheme,
    uploadedMedia,
    addMedia,
    removeMedia,
    saveTheme,
    savedCustomThemes,
    saveCustomTheme,
    deleteCustomTheme,
    hasUnsavedChanges
  } = useTheme();
  const [activeSection, setActiveSection] = useState<'prebuilt' | 'colors' | 'typography' | 'components' | 'images'>('prebuilt');
  const [customTheme, setCustomTheme] = useState<ThemeConfig>({
    ...currentTheme
  });
  const [showPreview, setShowPreview] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [customThemeName, setCustomThemeName] = useState('');
  // Sync customTheme with currentTheme when currentTheme changes externally (e.g. on load)
  useEffect(() => {
    setCustomTheme({
      ...currentTheme
    });
  }, [currentTheme]);
  const updateCustomColor = (key: keyof ThemeConfig['colors'], value: string) => {
    const updated = {
      ...customTheme,
      colors: {
        ...customTheme.colors,
        [key]: value
      }
    };
    setCustomTheme(updated);
    setTheme(updated);
  };
  const updateCustomFont = (key: keyof ThemeConfig['fonts'], value: string) => {
    const updated = {
      ...customTheme,
      fonts: {
        ...customTheme.fonts,
        [key]: value
      }
    };
    setCustomTheme(updated);
    setTheme(updated);
  };
  const applyPrebuilt = (theme: ThemeConfig) => {
    setCustomTheme({
      ...theme
    });
    setTheme(theme);
  };
  const handleReset = () => {
    resetTheme();
    setCustomTheme({
      ...prebuiltThemes[0]
    });
  };
  const updateImage = (key: keyof ThemeConfig['images'], value: string) => {
    const updated = {
      ...customTheme,
      images: {
        ...customTheme.images,
        [key]: value
      }
    };
    setCustomTheme(updated);
    setTheme(updated);
  };
  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = e => {
        const url = e.target?.result as string;
        const sizeKB = Math.round(file.size / 1024);
        const sizeStr = sizeKB > 1024 ? `${(sizeKB / 1024).toFixed(1)} MB` : `${sizeKB} KB`;
        addMedia({
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          name: file.name,
          url,
          size: sizeStr,
          uploadedAt: new Date().toISOString()
        });
      };
      reader.readAsDataURL(file);
    });
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };
  const handleSaveApply = async () => {
    try {
      setIsSaving(true);
      await saveTheme(customTheme);
      toast.success('Theme saved and applied successfully!');
    } catch (error) {
      toast.error('Failed to save theme.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveCustom = async () => {
    if (!customThemeName.trim()) return;
    try {
      await saveCustomTheme(customThemeName);
      toast.success('Custom theme saved!');
      setShowSaveModal(false);
      setCustomThemeName('');
    } catch (error) {
      toast.error('Failed to save custom theme.');
    }
  };
  const sections = [{
    id: 'prebuilt' as const,
    label: 'Prebuilt Themes',
    icon: Sparkles
  }, {
    id: 'colors' as const,
    label: 'Colors',
    icon: Palette
  }, {
    id: 'images' as const,
    label: 'Images & Uploads',
    icon: ImageIcon
  }, {
    id: 'typography' as const,
    label: 'Typography',
    icon: Type
  }, {
    id: 'components' as const,
    label: 'Components',
    icon: Square
  }];
  const fontOptions = ['Inter', 'Outfit', 'Poppins', 'Space Grotesk', 'DM Sans', 'Plus Jakarta Sans', 'Manrope', 'Sora'];
  return <div className="space-y-8 pb-32" data-id="element-1249">
      <Toaster theme="dark" position="top-right" data-id="element-1250" />
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1251">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} data-id="element-1252">
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1253">
            Theme Editor
          </h1>
          <p className="text-slate-400" data-id="element-1254">
            Customize the look and feel of your entire site.
          </p>
        </motion.div>
        <div className="flex gap-3" data-id="element-1255">
          <button onClick={() => setShowPreview(!showPreview)} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-1256">
            <Eye size={16} data-id="element-1257" /> Preview
          </button>
          <button onClick={handleReset} className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-1258">
            <RotateCcw size={16} data-id="element-1259" /> Reset
          </button>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-slate-800 pb-4 overflow-x-auto" data-id="element-1260">
        {sections.map(s => <button key={s.id} onClick={() => setActiveSection(s.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${activeSection === s.id ? 'bg-fox-600 text-white' : 'bg-slate-900 text-slate-400 hover:bg-slate-800 hover:text-white'}`} data-id="element-1261">
            <s.icon size={16} data-id="element-1262" /> {s.label}
          </button>)}
      </div>

      {/* Live Preview Bar */}
      <AnimatePresence data-id="element-1263">
        {showPreview && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="overflow-hidden" data-id="element-1264">
            <div className="rounded-2xl border overflow-hidden" style={{
          borderColor: customTheme.colors.border
        }} data-id="element-1265">
              {/* Mini Navbar Preview */}
              <div className="px-6 py-3 flex items-center justify-between" style={{
            backgroundColor: customTheme.colors.bgSecondary,
            borderBottom: `1px solid ${customTheme.colors.border}`
          }} data-id="element-1266">
                <span style={{
              color: customTheme.colors.textPrimary,
              fontFamily: customTheme.fonts.heading,
              fontWeight: 700
            }} data-id="element-1267">
                  Fox Line{' '}
                  <span style={{
                color: customTheme.colors.primary
              }} data-id="element-1268">
                    Hosting
                  </span>
                </span>
                <div className="flex items-center gap-4" data-id="element-1269">
                  <span style={{
                color: customTheme.colors.textSecondary,
                fontSize: '0.875rem'
              }} data-id="element-1270">
                    Features
                  </span>
                  <span style={{
                color: customTheme.colors.textSecondary,
                fontSize: '0.875rem'
              }} data-id="element-1271">
                    Pricing
                  </span>
                  <button style={{
                backgroundColor: customTheme.colors.primary,
                color: '#fff',
                padding: '0.375rem 1rem',
                borderRadius: customTheme.buttonStyle === 'pill' ? '9999px' : customTheme.buttonStyle === 'square' ? '0.25rem' : '0.5rem',
                fontSize: '0.75rem',
                fontWeight: 600
              }} data-id="element-1272">
                    Login
                  </button>
                </div>
              </div>

              {/* Mini Hero Preview */}
              <div className="px-6 py-10 text-center" style={{
            backgroundColor: customTheme.colors.bgPrimary
          }} data-id="element-1273">
                <h2 style={{
              color: customTheme.colors.textPrimary,
              fontFamily: customTheme.fonts.heading,
              fontSize: '1.5rem',
              fontWeight: 700,
              marginBottom: '0.5rem'
            }} data-id="element-1274">
                  Hosting That Runs{' '}
                  <span style={{
                color: customTheme.colors.primary
              }} data-id="element-1275">
                    Like a Fox
                  </span>
                </h2>
                <p style={{
              color: customTheme.colors.textMuted,
              fontSize: '0.875rem',
              marginBottom: '1rem'
            }} data-id="element-1276">
                  Lightning-fast, ultra-reliable web hosting.
                </p>
                <button style={{
              backgroundColor: customTheme.colors.primary,
              color: '#fff',
              padding: '0.5rem 1.5rem',
              borderRadius: customTheme.buttonStyle === 'pill' ? '9999px' : customTheme.buttonStyle === 'square' ? '0.25rem' : '0.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              boxShadow: `0 0 20px ${customTheme.colors.primaryGlow}`
            }} data-id="element-1277">
                  Get Started
                </button>
              </div>

              {/* Mini Cards Preview */}
              <div className="px-6 py-6 grid grid-cols-3 gap-3" style={{
            backgroundColor: customTheme.colors.bgSecondary
          }} data-id="element-1278">
                {['Web Hosting', 'VPS Servers', 'SSL Certs'].map((name, i) => <div key={i} className="p-4 text-center" style={{
              backgroundColor: customTheme.cardStyle === 'glass' ? `${customTheme.colors.bgSurface}99` : customTheme.colors.bgCard,
              border: `1px solid ${customTheme.colors.border}`,
              borderRadius: customTheme.borderRadius,
              boxShadow: customTheme.cardStyle === 'raised' ? '0 4px 20px rgba(0,0,0,0.3)' : 'none',
              backdropFilter: customTheme.cardStyle === 'glass' ? 'blur(10px)' : 'none'
            }} data-id="element-1279">
                    <div className="w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center" style={{
                backgroundColor: `${customTheme.colors.primary}20`
              }} data-id="element-1280">
                      <div className="w-4 h-4 rounded" style={{
                  backgroundColor: customTheme.colors.primary
                }} data-id="element-1281" />
                    </div>
                    <p style={{
                color: customTheme.colors.textPrimary,
                fontSize: '0.75rem',
                fontWeight: 600
              }} data-id="element-1282">
                      {name}
                    </p>
                  </div>)}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Prebuilt Themes */}
      {activeSection === 'prebuilt' && <div className="space-y-8" data-id="element-1283">
          {savedCustomThemes.length > 0 && <div data-id="element-1284">
              <h3 className="text-xl font-bold text-white mb-4" data-id="element-1285">
                My Saved Themes
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-1286">
                {savedCustomThemes.map(theme => <motion.div key={theme.id} whileHover={{
            y: -4
          }} onClick={() => applyPrebuilt(theme)} className={`group cursor-pointer rounded-2xl border overflow-hidden transition-all relative ${currentTheme.id === theme.id ? 'border-fox-500 ring-2 ring-fox-500/20' : 'border-slate-800 hover:border-slate-700'}`} data-id="element-1287">
                    <div className="h-28 relative" style={{
              backgroundColor: theme.colors.bgPrimary
            }} data-id="element-1288">
                      <div className="absolute inset-x-0 top-0 h-8 flex items-center px-4" style={{
                backgroundColor: theme.colors.bgSecondary
              }} data-id="element-1289">
                        <div className="w-2 h-2 rounded-full" style={{
                  backgroundColor: theme.colors.primary
                }} data-id="element-1290" />
                        <div className="ml-2 w-12 h-1.5 rounded" style={{
                  backgroundColor: theme.colors.textMuted
                }} data-id="element-1291" />
                      </div>
                      <div className="absolute bottom-4 left-4 right-4 flex gap-2" data-id="element-1292">
                        {[theme.colors.primary, theme.colors.accent, theme.colors.bgSurface, theme.colors.textPrimary].map((c, i) => <div key={i} className="w-6 h-6 rounded-full border border-white/20" style={{
                  backgroundColor: c
                }} data-id="element-1293" />)}
                      </div>
                      {currentTheme.id === theme.id && <div className="absolute top-2 right-2 w-6 h-6 bg-fox-500 rounded-full flex items-center justify-center" data-id="element-1294">
                          <Check size={14} className="text-white" data-id="element-1295" />
                        </div>}
                    </div>
                    <div className="p-4 flex items-center justify-between" style={{
              backgroundColor: theme.colors.bgSecondary
            }} data-id="element-1296">
                      <div data-id="element-1297">
                        <h3 className="text-white font-bold" data-id="element-1298">{theme.name}</h3>
                        <p className="text-slate-400 text-xs mt-1" data-id="element-1299">
                          {theme.navStyle} nav • {theme.cardStyle} cards
                        </p>
                      </div>
                      <button onClick={e => {
                e.stopPropagation();
                deleteCustomTheme(theme.id);
              }} className="w-8 h-8 rounded-full bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all" data-id="element-1300">
                        <Trash2 size={14} data-id="element-1301" />
                      </button>
                    </div>
                  </motion.div>)}
              </div>
            </div>}

          <div data-id="element-1302">
            <h3 className="text-xl font-bold text-white mb-4" data-id="element-1303">
              Prebuilt Themes
            </h3>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-1304">
              {prebuiltThemes.map(theme => <motion.div key={theme.id} whileHover={{
            y: -4
          }} onClick={() => applyPrebuilt(theme)} className={`cursor-pointer rounded-2xl border overflow-hidden transition-all ${currentTheme.id === theme.id ? 'border-fox-500 ring-2 ring-fox-500/20' : 'border-slate-800 hover:border-slate-700'}`} data-id="element-1305">
                  {/* Theme Preview Swatch */}
                  <div className="h-28 relative" style={{
              backgroundColor: theme.colors.bgPrimary
            }} data-id="element-1306">
                    <div className="absolute inset-x-0 top-0 h-8 flex items-center px-4" style={{
                backgroundColor: theme.colors.bgSecondary
              }} data-id="element-1307">
                      <div className="w-2 h-2 rounded-full" style={{
                  backgroundColor: theme.colors.primary
                }} data-id="element-1308" />
                      <div className="ml-2 w-12 h-1.5 rounded" style={{
                  backgroundColor: theme.colors.textMuted
                }} data-id="element-1309" />
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 flex gap-2" data-id="element-1310">
                      {[theme.colors.primary, theme.colors.accent, theme.colors.bgSurface, theme.colors.textPrimary].map((c, i) => <div key={i} className="w-6 h-6 rounded-full border border-white/20" style={{
                  backgroundColor: c
                }} data-id="element-1311" />)}
                    </div>
                    {currentTheme.id === theme.id && <div className="absolute top-2 right-2 w-6 h-6 bg-fox-500 rounded-full flex items-center justify-center" data-id="element-1312">
                        <Check size={14} className="text-white" data-id="element-1313" />
                      </div>}
                  </div>
                  <div className="p-4" style={{
              backgroundColor: theme.colors.bgSecondary
            }} data-id="element-1314">
                    <h3 className="text-white font-bold" data-id="element-1315">{theme.name}</h3>
                    <p className="text-slate-400 text-xs mt-1" data-id="element-1316">
                      {theme.navStyle} nav • {theme.cardStyle} cards •{' '}
                      {theme.buttonStyle} buttons
                    </p>
                  </div>
                </motion.div>)}
            </motion.div>
          </div>
        </div>}

      {/* Color Editor */}
      {activeSection === 'colors' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1317">
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-5" data-id="element-1318">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-1319">Brand Colors</h3>
            <ColorPicker label="Primary" value={customTheme.colors.primary} onChange={v => updateCustomColor('primary', v)} data-id="element-1320" />
            <ColorPicker label="Primary Hover" value={customTheme.colors.primaryHover} onChange={v => updateCustomColor('primaryHover', v)} data-id="element-1321" />
            <ColorPicker label="Accent" value={customTheme.colors.accent} onChange={v => updateCustomColor('accent', v)} data-id="element-1322" />
            <ColorPicker label="Accent Hover" value={customTheme.colors.accentHover} onChange={v => updateCustomColor('accentHover', v)} data-id="element-1323" />
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-5" data-id="element-1324">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-1325">
              Background Colors
            </h3>
            <ColorPicker label="Primary BG" value={customTheme.colors.bgPrimary} onChange={v => updateCustomColor('bgPrimary', v)} data-id="element-1326" />
            <ColorPicker label="Secondary BG" value={customTheme.colors.bgSecondary} onChange={v => updateCustomColor('bgSecondary', v)} data-id="element-1327" />
            <ColorPicker label="Surface" value={customTheme.colors.bgSurface} onChange={v => updateCustomColor('bgSurface', v)} data-id="element-1328" />
            <ColorPicker label="Card BG" value={customTheme.colors.bgCard} onChange={v => updateCustomColor('bgCard', v)} data-id="element-1329" />
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-5" data-id="element-1330">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-1331">Text Colors</h3>
            <ColorPicker label="Primary Text" value={customTheme.colors.textPrimary} onChange={v => updateCustomColor('textPrimary', v)} data-id="element-1332" />
            <ColorPicker label="Secondary Text" value={customTheme.colors.textSecondary} onChange={v => updateCustomColor('textSecondary', v)} data-id="element-1333" />
            <ColorPicker label="Muted Text" value={customTheme.colors.textMuted} onChange={v => updateCustomColor('textMuted', v)} data-id="element-1334" />
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6 space-y-5" data-id="element-1335">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-1336">Border Colors</h3>
            <ColorPicker label="Border" value={customTheme.colors.border} onChange={v => updateCustomColor('border', v)} data-id="element-1337" />
            <ColorPicker label="Border Hover" value={customTheme.colors.borderHover} onChange={v => updateCustomColor('borderHover', v)} data-id="element-1338" />
            <ColorPicker label="Primary Glow" value={customTheme.colors.primaryGlow.startsWith('rgba') ? '#ea580c' : customTheme.colors.primaryGlow} onChange={v => {
          const r = parseInt(v.slice(1, 3), 16);
          const g = parseInt(v.slice(3, 5), 16);
          const b = parseInt(v.slice(5, 7), 16);
          updateCustomColor('primaryGlow', `rgba(${r},${g},${b},0.3)`);
        }} data-id="element-1339" />
          </div>
        </motion.div>}

      {/* Typography */}
      {activeSection === 'typography' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-1340">
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-1341">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-1342">Heading Font</h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-1343">
              {fontOptions.map(font => <button key={font} onClick={() => updateCustomFont('heading', font)} className={`p-4 rounded-xl border text-left transition-all ${customTheme.fonts.heading === font ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-1344">
                  <span className="text-white font-bold text-lg" style={{
              fontFamily: font
            }} data-id="element-1345">
                    {font}
                  </span>
                  <p className="text-slate-500 text-xs mt-1" style={{
              fontFamily: font
            }} data-id="element-1346">
                    The quick brown fox
                  </p>
                </button>)}
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-1347">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-1348">Body Font</h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-1349">
              {fontOptions.map(font => <button key={font} onClick={() => updateCustomFont('body', font)} className={`p-4 rounded-xl border text-left transition-all ${customTheme.fonts.body === font ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-1350">
                  <span className="text-white font-medium" style={{
              fontFamily: font
            }} data-id="element-1351">
                    {font}
                  </span>
                  <p className="text-slate-500 text-xs mt-1" style={{
              fontFamily: font
            }} data-id="element-1352">
                    The quick brown fox jumps over the lazy dog.
                  </p>
                </button>)}
            </div>
          </div>
        </motion.div>}

      {/* Component Styles */}
      {activeSection === 'components' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1353">
          {/* Navbar Style */}
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-1354">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-1355">Navbar Style</h3>
            <div className="space-y-3" data-id="element-1356">
              {(['transparent', 'solid', 'glass'] as const).map(style => <button key={style} onClick={() => {
            const u = {
              ...customTheme,
              navStyle: style
            };
            setCustomTheme(u);
            setTheme(u);
          }} className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-3 ${customTheme.navStyle === style ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-1357">
                  <div className={`w-10 h-6 rounded ${style === 'transparent' ? 'bg-transparent border border-dashed border-slate-600' : style === 'solid' ? 'bg-slate-700' : 'bg-slate-700/50 backdrop-blur'}`} data-id="element-1358" />
                  <div data-id="element-1359">
                    <span className="text-white font-medium capitalize" data-id="element-1360">
                      {style}
                    </span>
                    <p className="text-slate-500 text-xs" data-id="element-1361">
                      {style === 'transparent' ? 'Blends with hero' : style === 'solid' ? 'Solid background' : 'Frosted glass effect'}
                    </p>
                  </div>
                </button>)}
            </div>
          </div>

          {/* Card Style */}
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-1362">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-1363">Card Style</h3>
            <div className="space-y-3" data-id="element-1364">
              {(['flat', 'raised', 'glass', 'bordered'] as const).map(style => <button key={style} onClick={() => {
            const u = {
              ...customTheme,
              cardStyle: style
            };
            setCustomTheme(u);
            setTheme(u);
          }} className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-3 ${customTheme.cardStyle === style ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-1365">
                    <div className={`w-10 h-8 rounded-lg ${style === 'flat' ? 'bg-slate-800' : style === 'raised' ? 'bg-slate-800 shadow-lg shadow-black/30' : style === 'glass' ? 'bg-slate-800/50 border border-slate-700' : 'bg-transparent border-2 border-slate-600'}`} data-id="element-1366" />
                    <div data-id="element-1367">
                      <span className="text-white font-medium capitalize" data-id="element-1368">
                        {style}
                      </span>
                      <p className="text-slate-500 text-xs" data-id="element-1369">
                        {style === 'flat' ? 'Clean, no shadow' : style === 'raised' ? 'Elevated with shadow' : style === 'glass' ? 'Translucent blur' : 'Outline only'}
                      </p>
                    </div>
                  </button>)}
            </div>
          </div>

          {/* Button & Radius */}
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-6" data-id="element-1370">
            <h3 className="text-lg font-bold text-white mb-6" data-id="element-1371">Button Style</h3>
            <div className="space-y-3 mb-8" data-id="element-1372">
              {(['rounded', 'pill', 'square'] as const).map(style => <button key={style} onClick={() => {
            const u = {
              ...customTheme,
              buttonStyle: style
            };
            setCustomTheme(u);
            setTheme(u);
          }} className={`w-full p-4 rounded-xl border text-left transition-all flex items-center gap-3 ${customTheme.buttonStyle === style ? 'border-fox-500 bg-fox-500/10' : 'border-slate-800 hover:border-slate-700 bg-slate-900'}`} data-id="element-1373">
                  {style === 'rounded' ? <RectangleHorizontal size={20} className="text-slate-400" data-id="element-1374" /> : style === 'pill' ? <Circle size={20} className="text-slate-400" data-id="element-1375" /> : <Square size={20} className="text-slate-400" data-id="element-1376" />}
                  <span className="text-white font-medium capitalize" data-id="element-1377">
                    {style}
                  </span>
                </button>)}
            </div>

            <h3 className="text-lg font-bold text-white mb-4" data-id="element-1378">Border Radius</h3>
            <input type="range" min="0" max="2" step="0.25" value={parseFloat(customTheme.borderRadius)} onChange={e => {
          const u = {
            ...customTheme,
            borderRadius: `${e.target.value}rem`
          };
          setCustomTheme(u);
          setTheme(u);
        }} className="w-full accent-fox-500" data-id="element-1379" />
            <div className="flex justify-between text-xs text-slate-500 mt-1" data-id="element-1380">
              <span data-id="element-1381">Sharp</span>
              <span data-id="element-1382">{customTheme.borderRadius}</span>
              <span data-id="element-1383">Round</span>
            </div>
          </div>
        </motion.div>}

      {/* Images & Uploads */}
      {activeSection === 'images' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="space-y-8" data-id="element-1384">
          {/* Upload Area */}
          <div onDragOver={e => {
        e.preventDefault();
        setDragOver(true);
      }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} className={`border-2 border-dashed rounded-2xl p-10 text-center transition-colors ${dragOver ? 'border-fox-500 bg-fox-500/5' : 'border-slate-700 hover:border-slate-600 bg-slate-950'}`} data-id="element-1385">
            <Upload size={40} className={`mx-auto mb-4 ${dragOver ? 'text-fox-500' : 'text-slate-600'}`} data-id="element-1386" />
            <h3 className="text-white font-bold text-lg mb-2" data-id="element-1387">Upload Images</h3>
            <p className="text-slate-400 text-sm mb-4" data-id="element-1388">
              Drag & drop images here, or click to browse
            </p>
            <label className="inline-flex items-center gap-2 bg-fox-600 hover:bg-fox-500 text-white px-5 py-2.5 rounded-lg font-medium cursor-pointer transition-colors" data-id="element-1389">
              <ImageIcon size={16} data-id="element-1390" /> Choose Files
              <input type="file" accept="image/*" multiple className="hidden" onChange={e => handleFileUpload(e.target.files)} data-id="element-1391" />
            </label>
            <p className="text-slate-600 text-xs mt-3" data-id="element-1392">
              PNG, JPG, SVG, WebP up to 5MB each
            </p>
          </div>

          {/* Media Library */}
          <div data-id="element-1393">
            <div className="flex items-center justify-between mb-4" data-id="element-1394">
              <h3 className="text-lg font-bold text-white" data-id="element-1395">
                Uploaded Media Library
              </h3>
              <span className="text-sm text-slate-500" data-id="element-1396">
                {uploadedMedia.length} file
                {uploadedMedia.length !== 1 ? 's' : ''}
              </span>
            </div>

            {uploadedMedia.length === 0 ? <div className="bg-slate-950 border border-slate-800 rounded-2xl p-12 text-center" data-id="element-1397">
                <ImageIcon size={48} className="mx-auto text-slate-700 mb-4" data-id="element-1398" />
                <p className="text-slate-500" data-id="element-1399">
                  No images uploaded yet. Upload images above to use them in
                  your theme.
                </p>
              </div> : <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-1400">
                {uploadedMedia.map(media => <div key={media.id} className="group relative bg-slate-950 border border-slate-800 rounded-xl overflow-hidden hover:border-slate-700 transition-colors" data-id="element-1401">
                    <div className="aspect-square bg-slate-900" data-id="element-1402">
                      <img src={media.url} alt={media.name} className="w-full h-full object-cover" data-id="element-1403" />
                    </div>
                    <div className="p-3" data-id="element-1404">
                      <p className="text-white text-xs font-medium truncate" data-id="element-1405">
                        {media.name}
                      </p>
                      <p className="text-slate-500 text-[10px]" data-id="element-1406">{media.size}</p>
                    </div>
                    <button onClick={() => removeMedia(media.id)} className="absolute top-2 right-2 w-7 h-7 bg-red-500/80 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1407">
                      <Trash2 size={12} data-id="element-1408" />
                    </button>
                  </div>)}
              </div>}
          </div>

          {/* Section Image Assignments */}
          <div data-id="element-1409">
            <h3 className="text-lg font-bold text-white mb-4" data-id="element-1410">
              Assign Images to Site Sections
            </h3>
            <p className="text-slate-400 text-sm mb-6" data-id="element-1411">
              Select uploaded images or paste URLs to use as backgrounds for
              different sections of your site.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1412">
              {[{
            key: 'heroBg' as const,
            label: 'Hero Background',
            desc: 'Main landing hero section background image'
          }, {
            key: 'featuresBg' as const,
            label: 'Features Background',
            desc: 'Features section background image'
          }, {
            key: 'pricingBg' as const,
            label: 'Pricing Background',
            desc: 'Pricing section background image'
          }, {
            key: 'loginBg' as const,
            label: 'Login Background',
            desc: 'Login page background image'
          }, {
            key: 'logo' as const,
            label: 'Site Logo',
            desc: 'Replace the fox logo with a custom image'
          }].map(({
            key,
            label,
            desc
          }) => <div key={key} className="bg-slate-950 border border-slate-800 rounded-2xl p-5" data-id="element-1413">
                  <div className="flex items-start justify-between mb-4" data-id="element-1414">
                    <div data-id="element-1415">
                      <h4 className="text-white font-bold" data-id="element-1416">{label}</h4>
                      <p className="text-slate-500 text-xs mt-1" data-id="element-1417">{desc}</p>
                    </div>
                    {customTheme.images[key] && <button onClick={() => updateImage(key, '')} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1" data-id="element-1418">
                        <XIcon size={12} data-id="element-1419" /> Remove
                      </button>}
                  </div>

                  {/* Current Image Preview */}
                  {customTheme.images[key] ? <div className="mb-4 rounded-lg overflow-hidden border border-slate-800 aspect-video bg-slate-900" data-id="element-1420">
                      <img src={customTheme.images[key]} alt={label} className="w-full h-full object-cover" data-id="element-1421" />
                    </div> : <div className="mb-4 rounded-lg border border-dashed border-slate-700 aspect-video bg-slate-900 flex items-center justify-center" data-id="element-1422">
                      <span className="text-slate-600 text-sm" data-id="element-1423">
                        No image set
                      </span>
                    </div>}

                  {/* URL Input */}
                  <div className="space-y-3" data-id="element-1424">
                    <input type="text" value={customTheme.images[key]} onChange={e => updateImage(key, e.target.value)} placeholder="Paste image URL..." className="w-full bg-slate-900 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:border-fox-500 focus:outline-none" data-id="element-1425" />

                    {/* Quick Select from Library */}
                    {uploadedMedia.length > 0 && <div data-id="element-1426">
                        <p className="text-xs text-slate-500 mb-2" data-id="element-1427">
                          Or select from library:
                        </p>
                        <div className="flex gap-2 overflow-x-auto pb-1" data-id="element-1428">
                          {uploadedMedia.map(media => <button key={media.id} onClick={() => updateImage(key, media.url)} className={`shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-colors ${customTheme.images[key] === media.url ? 'border-fox-500' : 'border-slate-700 hover:border-slate-600'}`} data-id="element-1429">
                              <img src={media.url} alt={media.name} className="w-full h-full object-cover" data-id="element-1430" />
                            </button>)}
                        </div>
                      </div>}
                  </div>
                </div>)}
            </div>
          </div>
        </motion.div>}

      {/* Sticky Bottom Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-slate-950/80 backdrop-blur-xl border-t border-slate-800 p-4 z-40" data-id="element-1431">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-1432">
          <div className="flex items-center gap-3" data-id="element-1433">
            {hasUnsavedChanges ? <>
                <div className="relative flex h-3 w-3" data-id="element-1434">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" data-id="element-1435"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500" data-id="element-1436"></span>
                </div>
                <span className="text-amber-500 text-sm font-medium" data-id="element-1437">
                  Unsaved changes
                </span>
              </> : <span className="text-slate-500 text-sm" data-id="element-1438">
                Theme is up to date
              </span>}
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-1439">
            <div className="relative" data-id="element-1440">
              <button onClick={() => setShowSaveModal(!showSaveModal)} className="w-full sm:w-auto px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm font-medium transition-colors" data-id="element-1441">
                Save As Custom Theme
              </button>

              {/* Save Modal Popover */}
              <AnimatePresence data-id="element-1442">
                {showSaveModal && <motion.div initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 10
              }} className="absolute bottom-full right-0 mb-2 w-64 bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-xl" data-id="element-1443">
                    <h4 className="text-white font-bold mb-2" data-id="element-1444">
                      Save Custom Theme
                    </h4>
                    <input type="text" value={customThemeName} onChange={e => setCustomThemeName(e.target.value)} placeholder="Theme Name" className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:border-fox-500 focus:outline-none mb-3" autoFocus data-id="element-1445" />
                    <div className="flex gap-2" data-id="element-1446">
                      <button onClick={() => setShowSaveModal(false)} className="flex-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm transition-colors" data-id="element-1447">
                        Cancel
                      </button>
                      <button onClick={handleSaveCustom} disabled={!customThemeName.trim()} className="flex-1 px-3 py-1.5 bg-fox-600 hover:bg-fox-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors" data-id="element-1448">
                        Save
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
            <button onClick={handleSaveApply} disabled={!hasUnsavedChanges || isSaving} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2 bg-fox-600 hover:bg-fox-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors" data-id="element-1449">
              <Save size={16} data-id="element-1450" />
              {isSaving ? 'Saving...' : 'Save & Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>;
}